import {
  builderAdd,
  builderEndsRefuse,
  builderLink,
  builderPlace,
  builderPlan,
  builderProblem,
  builderSetPhase,
  builderTreeCompleteness,
  builderDeadEnds,
  builderLoose,
  builderUnlink,
  isAside,
  titleCase,
  wouldLoopBuilder,
  type BuilderKind,
  type BuilderTree,
} from '@nsb/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api, errorText, queryClient } from '../../api.ts';
import { NodeCard } from '../../components/builder/NodeCard.tsx';
import { PhaseCard } from '../../components/builder/PhaseCard.tsx';
import { PhasesEditor } from '../../components/builder/PhasesEditor.tsx';
import { PrototypeFunnel } from '../../components/builder/PrototypeFunnel.tsx';
import { LockIn, MasterVersion } from '../../components/process/LockIn.tsx';
import { StepPictures } from '../../components/process/StepPictures.tsx';
import { useStepIcons } from '../../components/process/stepIcons.ts';
import { masterChoiceRefusal, masterChoices, type Side } from '../../components/process/needsOpens.ts';
import { lockInKey, useTrades } from '../../components/process/useProcess.ts';
import { masterTiles, onBoard } from '../../components/tree/master.ts';
import { cardPhase, linesOf, type TileLine } from '../../components/tree/model.ts';
import { TOOL_KINDS, type ToolKind } from '../../components/tree/kinds.ts';
import { TreeTools } from '../../components/tree/Tools.tsx';
import { TreeView } from '../../components/tree/TreeView.tsx';
import { Button, CompletenessBar, Loading, Page, Title } from '../../components/ui.tsx';
import { useWide } from '../../components/useWide.ts';
import { easternToday } from '../../days.ts';
import { isAdmin, useMe } from '../../session.tsx';
import { untilKept } from './draftKept.ts';

/**
 * Settings → Process tree: the organization's tree, the one jobs are made from once it is locked in (design studies
 * 09 and 10, as Jason answered them, 2026-10-06). At the top the prototypical funnel the tree makes, how much of it is
 * configured, its pictures and its lock-in; then the tools (a kind dragged into a phase adds a step; Find a step rings
 * one) and the tree itself, full size and never zoomed: each phase a block the width of the page, its steps as tiles in
 * rows on the page's columns, every line faint (TreeBoard). It lays itself out: a step's row is the longest way back to
 * it within its phase; a line is drawn from a step's foot down onto what follows it; a step dragged into another block
 * moves to that phase. A click opens a step with its chain alone, its card in the middle, where its lines can be taken
 * out. A click on a phase's heading opens the phase's card (PhaseCard; Jason, 2026-10-07), where its facts are set: its
 * name, what happens in it, the gate that closes it, how sure the price is once it closes and its share of the time.
 * On a phone, the phases as blocks of rows, a tap opening a step's card; what is saved and Undo show there too.
 * Drawn, opened and closed as every tree is (TreeView). The tree is the organization's draft (`routes/tree-builder.ts`),
 * saved as it changes; Claude reads and answers it with its key.
 */

interface BuilderDraft {
  tree: BuilderTree;
  etag: string;
}

type Saving = 'saved' | 'waiting' | 'saving' | 'stale' | 'error';
const q = (t: string) => `“${titleCase(t)}”`;
const typing = (el: EventTarget | null) => el instanceof HTMLElement && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
/** The new board lays itself out: the boxes the old paper drew phases with are gone with it, so none decides a phase. */
const boxless = (t: BuilderTree): BuilderTree => (t.phases.some((p) => p.at) ? { ...t, phases: t.phases.map((p) => ({ ...p, at: null })) } : t);
/** Every step is on the board (it lays itself out): a new one, or one in the old list, sits anywhere. */
const ON_BOARD = { x: 0, y: 0 };

export function TreeBuilderSettings() {
  const { data: me } = useMe();
  const icons = useStepIcons();
  const pictures = icons.urls;
  const { data: tradesDto } = useTrades();
  const wide = useWide();
  const [tree, setTree] = useState<BuilderTree | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [undo, setUndo] = useState<{ tree: BuilderTree; words: string }[]>([]);
  const [status, setStatus] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const [found, setFound] = useState<string | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const [arming, setArming] = useState<ToolKind | null>(null);
  const [asked, setAsked] = useState<TileLine | null>(null);
  // "+ It needs…" or "+ It opens…" pressed on a step's card: the step chosen on the board, or by its name (slice 5).
  const [choosing, setChoosing] = useState<{ key: string; side: Side } | null>(null);
  const [saving, setSaving] = useState<Saving>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  // What the saving works from, outside React's renders: the latest tree, the version it was loaded as, and how far the saves have got.
  const latest = useRef<BuilderTree | null>(null);
  const etag = useRef('');
  const version = useRef(0);
  const savedVersion = useRef(0);
  const inFlight = useRef(false);
  // The save on its way, for what waits on the draft holding every change so far (`untilKept`).
  const flight = useRef<Promise<void> | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const save = useCallback(function saveNow(): Promise<void> {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    if (inFlight.current) return flight.current ?? Promise.resolve();
    if (!latest.current) return Promise.resolve();
    if (savedVersion.current === version.current) {
      setSaving('saved');
      return Promise.resolve();
    }
    inFlight.current = true;
    const v = version.current;
    setSaving('saving');
    const put = (async () => {
      try {
        const r = await api<{ etag: string }>('/builder', { method: 'PUT', body: { tree: latest.current, etag: etag.current } });
        etag.current = r.etag;
        savedVersion.current = v;
        inFlight.current = false;
        flight.current = null;
        // What saving it for projects would change is read again.
        void queryClient.invalidateQueries({ queryKey: lockInKey });
        if (v === version.current) setSaving('saved');
        else void saveNow();
      } catch (e) {
        inFlight.current = false;
        flight.current = null;
        if (e instanceof ApiError && e.code === 'builder_stale') setSaving('stale');
        else {
          setSaving('error');
          setSaveError(errorText(e));
        }
      }
    })();
    flight.current = put;
    return put;
  }, []);

  /** Every change made here so far kept in the draft: what reads the draft (Paint again) is asked for only then. */
  const kept = useCallback(
    () => untilKept({ version: () => version.current, saved: () => savedVersion.current, flight: () => flight.current, save }),
    [save],
  );

  /** The draft as it is now, starting over from it: nothing to undo, nothing waiting to save. */
  const load = useCallback(() => {
    window.clearTimeout(timer.current);
    return api<BuilderDraft>('/builder').then(
      (r) => {
        latest.current = r.tree;
        etag.current = r.etag;
        version.current = 0;
        savedVersion.current = 0;
        setTree(r.tree);
        setUndo([]);
        setStatus(null);
        setSaving('saved');
        setLoadError(null);
      },
      (e: unknown) => setLoadError(errorText(e)),
    );
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A change made elsewhere (the phone, the desk, or Claude answering comments) is brought in when this page comes back
  // into view, while nothing here waits to be saved; never on a timer (Jason, 2026-10-05: "Why would it poll?"). The
  // version alone is asked first, the draft only when it changed; a save over a newer version is refused regardless.
  useEffect(() => {
    let looking = false;
    const look = () => {
      if (looking || document.hidden || inFlight.current || timer.current !== undefined || version.current !== savedVersion.current) return;
      looking = true;
      const at = version.current;
      void api<{ etag: string }>('/builder/version')
        .then(
          (v) => (v.etag === etag.current ? null : api<BuilderDraft>('/builder')),
          () => null,
        )
        .then(
          (r) => {
            if (!r || r.etag === etag.current || version.current !== at || timer.current !== undefined || inFlight.current) return;
            latest.current = r.tree;
            etag.current = r.etag;
            setTree(r.tree);
            // What was done before the draft changed is not undone over the change.
            setUndo([]);
            setStatus('The tree changed on another screen, or Claude answered: this is it now.');
            setSaving('saved');
          },
          () => undefined,
        )
        .finally(() => {
          looking = false;
        });
    };
    document.addEventListener('visibilitychange', look);
    window.addEventListener('focus', look);
    return () => {
      document.removeEventListener('visibilitychange', look);
      window.removeEventListener('focus', look);
    };
  }, []);

  // Nothing typed is lost: leaving the page saves what waits, and closing the tab asks first.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (version.current !== savedVersion.current) e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      if (timer.current !== undefined) void save();
    };
  }, [save]);

  /** The tree as it now is: shown, and saved shortly unless the draft changed underneath. */
  const apply = useCallback(
    (next: BuilderTree) => {
      latest.current = next;
      version.current += 1;
      setTree(next);
      setSaving((s) => (s === 'stale' ? s : 'waiting'));
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        void save();
      }, 700);
    },
    [save],
  );

  /** A change, in words, that Undo can take back; one that would break the tree is refused, saying why. */
  const edit = useCallback(
    (changed: BuilderTree, words: string) => {
      const before = latest.current;
      if (!before) return;
      const next = boxless(changed);
      const problem = builderProblem(next);
      if (problem) {
        setRefused(problem);
        return;
      }
      setUndo((u) => [...u.slice(-79), { tree: before, words }]);
      setStatus(words);
      setRefused(null);
      apply(next);
    },
    [apply],
  );

  const byKey = useMemo(() => new Map((tree?.nodes ?? []).map((n) => [n.key, n])), [tree]);

  const takeBack = useCallback(() => {
    const last = undo.at(-1);
    if (!last) return;
    setUndo(undo.slice(0, -1));
    setStatus(`Undone: ${last.words}`);
    setRefused(null);
    apply(last.tree);
  }, [undo, apply]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (typing(e.target)) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        takeBack();
      } else if (e.key === 'Escape' && !(focus && (byKey.has(focus) || cardPhase(focus) !== null))) {
        // The step opened first (TreeView's), then a kind chosen, then what was found.
        if (arming) setArming(null);
        else setFound(null);
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [takeBack, focus, arming, byKey]);

  const today = useMemo(() => easternToday(), []);
  const plan = useMemo(() => (tree ? builderPlan(tree, today) : null), [tree, today]);
  const configured = useMemo(() => (tree ? builderTreeCompleteness(tree) : null), [tree]);
  const loose = useMemo(() => (tree ? builderLoose(tree).size : 0), [tree]);
  const deadEnds = useMemo(() => (tree ? builderDeadEnds(tree).size : 0), [tree]);
  const trades = useMemo(() => tradesDto?.trades ?? [], [tradesDto]);
  const tradeName = useCallback((k: string) => trades.find((t) => t.key === k)?.name ?? null, [trades]);
  const tiles = useMemo(() => (tree ? masterTiles(tree, pictures, tradeName) : null), [tree, pictures, tradeName]);
  const lines = useMemo(() => (tiles ? linesOf(tiles.steps) : []), [tiles]);
  // What the typed field finds while choosing: the board's steps in the tree's walk, then the tasks in a step's list.
  const choices = useMemo(() => (tree && choosing ? masterChoices(tree) : []), [tree, choosing]);

  if (!isAdmin(me)) {
    return (
      <Page>
        <p className="py-10 text-ink-2">Nothing here.</p>
      </Page>
    );
  }
  if (loadError) {
    return (
      <Page>
        <Title kicker="Settings">Master process tree</Title>
        <p role="alert" className="text-ink-2">
          {loadError}
        </p>
        <Button tone="secondary" className="mt-4" onClick={() => void load()}>
          Try again
        </Button>
      </Page>
    );
  }
  if (!tree || !plan || !configured || !tiles) return <Loading />;

  const title = (k: string) => byKey.get(k)?.title ?? k;
  const focused = focus ? byKey.get(focus) : undefined;
  // A step opened, or a phase opened from its heading (its card).
  const openPhase = cardPhase(focus);
  const opened = focused?.key ?? (openPhase !== null && tree.phases.some((p) => p.key === openPhase) ? focus : null);
  // Steps left in the old list (the board laid out by hand had one): every step is on the board now.
  const unplaced = tree.nodes.filter((n) => n.parent === null && n.at === null && !isAside(n));
  const openFocus = (k: string | null) => {
    setFocus(k);
    setFresh(null);
    setAsked(null);
    setArming(null);
    setChoosing(null);
  };
  /**
   * Choosing what the step opened needs or opens (design study 31, slice 5): the board drawn whole, a step it cannot join
   * greyed with why (core `builderLinkRefusal`: a loop, a step in a later phase than one that would need it), the one
   * pressed or named joined to it in the draft, Undo taking it back; then its card again.
   */
  const choose =
    choosing && byKey.has(choosing.key) && focus === choosing.key
      ? {
          key: choosing.key,
          side: choosing.side,
          refusal: (k: string) => masterChoiceRefusal(tree, choosing.key, choosing.side, k),
          onPick: (k: string) => {
            const [from, to] = choosing.side === 'needs' ? [choosing.key, k] : [k, choosing.key];
            setChoosing(null);
            edit(builderLink(tree, from, to), `${q(title(from))} comes after ${q(title(to))} now.`);
          },
          onCancel: () => setChoosing(null),
          among: choices,
        }
      : undefined;
  /** Why `dependent` cannot wait on `needed`, or null. */
  const check = (dependent: string, needed: string): string | null => {
    const a = byKey.get(dependent);
    const b = byKey.get(needed);
    if (!a || !b) return null;
    const ends = builderEndsRefuse(tree, dependent, needed);
    if (ends) return ends;
    if (b.parent === dependent) return `${q(b.title)} is a task of ${q(a.title)}.`;
    if (wouldLoopBuilder(tree, dependent, needed)) return `${q(b.title)} already waits on ${q(a.title)}, all the way back: that would loop.`;
    return null;
  };
  const link = (dependent: string, needed: string, option: string | null) => {
    const why = check(dependent, needed);
    if (why) {
      setRefused(why);
      return;
    }
    const o = option ? byKey.get(needed)?.options.find((x) => x.id === option)?.text : null;
    edit(builderLink(tree, dependent, needed, option), `${q(title(dependent))} comes after ${q(title(needed))}${o ? `: ${q(o)}` : ''} now.`);
  };
  /** A new step of a kind, in a phase, its card open with its title ready to write (study 09, adding A). */
  const add = (kind: string, phase: string | null) => {
    const k = TOOL_KINDS.find((x) => x.kind === kind);
    const name = kind === 'decision' ? 'A new decision' : kind === 'gate' ? 'A new gate' : kind === 'milestone' ? 'A new milestone' : 'A new step';
    const made = builderAdd(boxless(tree), { title: name, kind: kind as BuilderKind, phase, at: ON_BOARD });
    edit(made.tree, `Added ${k?.as ?? 'a step'} to ${phase ? (tree.phases.find((p) => p.key === phase)?.name ?? phase) : 'no phase'}: ${q(name)}.`);
    openFocus(made.key);
    setFresh(made.key);
  };
  const phaseName = (k: string | null) => (k ? (tree.phases.find((p) => p.key === k)?.name ?? k) : 'no phase');
  // The draft, kept as it changes: saving it for projects is the bar above (LockIn).
  const saveWords: Record<Saving, string> = { saved: 'Draft kept', waiting: 'Keeping the draft…', saving: 'Keeping the draft…', stale: 'Draft not kept: the tree changed', error: 'Draft not kept' };
  const pct = configured.of ? Math.round((100 * configured.done) / configured.of) : 0;

  const saveState = (
    <span className="meta flex items-center gap-4" aria-live="polite">
      <span>
        {saveWords[saving]}
        {saving === 'error' && saveError ? `: ${saveError}` : ''}
      </span>
      {saving === 'error' ? (
        <button type="button" className="link" onClick={() => void save()}>
          Try again
        </button>
      ) : null}
      {undo.length ? (
        <Button tone="quiet" onClick={takeBack} title="Ctrl+Z">
          Undo
        </Button>
      ) : null}
    </span>
  );

  const sayLine = (
    <div className="flex min-h-11 flex-wrap items-center gap-x-4" aria-live="polite">
      {refused ? (
        <p role="alert" className="text-[0.9375rem] text-alert">
          {refused}
        </p>
      ) : status ? (
        <p className="text-[0.9375rem] text-ink-2">{status}</p>
      ) : null}
    </div>
  );

  // How much of the whole tree is configured (Jason, 2026-10-04: "an indicator bar as to the configuration state of the whole tree").
  const configBar = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <CompletenessBar done={configured.configured} of={configured.nodes} label={`${configured.configured} of ${configured.nodes} steps configured`} />
      <span className="text-[0.9375rem]">
        <span className="num">{configured.configured}</span> of <span className="num">{configured.nodes}</span> steps configured
      </span>
      <span className="meta">
        <span className="num">{pct}%</span> of their details filled in
      </span>
      {loose ? (
        <span className="rounded-[2px] bg-[#e00000] px-2 py-0.5 text-[0.9375rem] text-white">
          <span className="num">{loose}</span> with nothing coming in, in red
        </span>
      ) : null}
      {deadEnds ? (
        <span className="rounded-[2px] bg-[#0057ff] px-2 py-0.5 text-[0.9375rem] text-white">
          <span className="num">{deadEnds}</span> leading to nothing, in blue
        </span>
      ) : null}
    </div>
  );

  const card = (key: string) => {
    const phase = cardPhase(key);
    if (phase !== null) return <PhaseCard key={key} tree={tree} phase={phase} plan={plan} edit={edit} leave={() => openFocus(null)} />;
    const n = byKey.get(key);
    if (!n) return null;
    return (
      <NodeCard
        key={n.key}
        tree={tree}
        node={n}
        author={me?.user.name ?? me?.user.email ?? 'NSB'}
        picture={pictures.get(n.key) ?? null}
        trades={trades}
        fresh={fresh === n.key}
        edit={edit}
        focusOn={(k) => openFocus(k)}
        place={(k) => edit(builderPlace(boxless(tree), k, ON_BOARD), `${q(title(k))} is a step of its own now, on the board.`)}
        leave={() => openFocus(null)}
        kept={kept}
        onChoose={(side) => {
          setChoosing({ key: n.key, side });
          setFresh(null);
          setRefused(null);
          setAsked(null);
        }}
      />
    );
  };

  const stale =
    saving === 'stale' ? (
      <div role="alert" className="mt-3 border-l-2 border-alert bg-alert/5 px-4 py-3">
        <p>The tree changed since this page loaded it (on another screen, or by Claude answering a comment). What you have done since the last save is not in it.</p>
        <div className="mt-2 flex flex-wrap gap-x-6">
          <Button tone="quiet" onClick={() => void load()}>
            Load it again (losing what is here)
          </Button>
          <Button
            tone="danger"
            onClick={() => {
              void api<BuilderDraft>('/builder').then((r) => {
                etag.current = r.etag;
                setSaving('waiting');
                void save();
              });
            }}
          >
            Save this over it (losing that change)
          </Button>
        </div>
      </div>
    ) : null;

  const lineAsk = asked ? (
    <div role="dialog" aria-label="This line" className="sticky top-2 z-30 mb-3 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-[2px] border border-ink bg-paper px-4 py-2.5">
      <span>
        {q(title(asked.dst))} comes after {q(title(asked.src))}
        {asked.option ? `: ${q(byKey.get(asked.src)?.options.find((o) => o.id === asked.option)?.text ?? '')}` : ''}.
      </span>
      <button
        type="button"
        className="link"
        onClick={() => {
          edit(builderUnlink(tree, asked.dst, asked.src), `Took out: ${q(title(asked.dst))} comes after ${q(title(asked.src))}.`);
          setAsked(null);
        }}
      >
        Take it out
      </button>
      {asked.option ? null : (
        <button
          type="button"
          className="link"
          onClick={() => {
            const flipped = builderUnlink(tree, asked.dst, asked.src);
            if (wouldLoopBuilder(flipped, asked.src, asked.dst)) {
              setRefused(`${q(title(asked.src))} cannot come after ${q(title(asked.dst))}: that would loop.`);
              return;
            }
            edit(builderLink(flipped, asked.src, asked.dst), `Turned round: ${q(title(asked.src))} comes after ${q(title(asked.dst))} now.`);
            setAsked(null);
          }}
        >
          Turn it round
        </button>
      )}
      <button type="button" className="link text-ink-3" onClick={() => setAsked(null)}>
        Keep it
      </button>
    </div>
  ) : null;

  const unplacedLine = unplaced.length ? (
    <p className="mt-3 flex flex-wrap items-center gap-x-4 text-ink-2">
      {unplaced.length} {unplaced.length === 1 ? 'step is' : 'steps are'} not on the board yet (they were in the list the old board had).
      <Button tone="quiet" onClick={() => edit(unplaced.reduce((t, n) => builderPlace(t, n.key, ON_BOARD), boxless(tree)), `Put ${unplaced.length} ${unplaced.length === 1 ? 'step' : 'steps'} on the board.`)}>
        Put them on the board
      </Button>
    </p>
  ) : null;

  return (
    <Page full={wide}>
      <Title kicker="Settings">
        Master process tree
        <MasterVersion editable={isAdmin(me)} />
      </Title>
      {/* What it is to every project (Jason, 2026-10-07: "very clearly say that this is the process tree that all projects inherit"). */}
      <p className="-mt-3 mb-4 max-w-measure text-ink-2">
        The tree every project inherits. What you change here reaches the steps on every project, except where a project has changed that step for itself, and only once you save the changes.
      </p>
      {/* What saving it for projects would change, and Save the changes, as a project's own changes are drawn (tech-tree spec v3 §8). */}
      <div className="mb-4">
        <LockIn editable={isAdmin(me)} onUndo={undo.length ? takeBack : undefined} onDiscarded={() => void load()} />
      </div>
      <PrototypeFunnel tree={tree} />
      <div className="mt-4">{configBar}</div>
      {/* Every step its picture (Jason, 2026-10-06): painted here, and sent to production with the tree (npm run builder -- push). */}
      {wide ? (
        <div className="mt-2">
          <StepPictures icons={icons} editable={isAdmin(me)} />
        </div>
      ) : null}
      {wide ? (
        <details className="mt-3 max-w-measure text-ink-2">
          <summary className="label cursor-pointer py-2">How it works</summary>
          <p>
            The organization’s master process tree, the same on every screen; every project inherits it as it was last saved (the bar above lists what is not saved yet). It lays itself out: each phase a block, its steps in rows, a step on the row of the longest way back to it within its phase, the gate that closes a phase at its foot, sitting on the next phase’s heading, which it opens. A line to a later phase is drawn through the gates between. A click on a phase’s heading opens its card, where its name, what happens in it, its gate, how sure the price is and its share of the time are set. Drag a kind from the tools into a phase to add a step. Draw a line from a step’s dot at its foot down onto what follows it; drag a step into another block to move it to that phase. A click opens a step with its chain alone and its card, where a line clicked can be taken out or turned round; a click on the paper round it, or Esc, goes back. A step is grey and dashed, without its picture, until everything its kind needs is filled in on its card.
          </p>
        </details>
      ) : null}
      {stale}
      {unplacedLine}
      {/* What is saved, and Undo, on a phone as on a wide screen (where it sits with the tools). */}
      {wide ? null : <div className="mt-3">{saveState}</div>}
      <div className="mt-4">
        <TreeView
          phases={tiles.phases}
          steps={tiles.steps}
          lines={lines}
          label="The process tree"
          open={opened}
          onOpen={openFocus}
          phaseCards
          card={card}
          // The master's card is where a step is set up, its checklist across it (L-38: "this whole thing needs to be
          // much wider"): wider than a job's or the client's.
          cardWidth={1120}
          titleOf={(k) => byKey.get(k)?.title ?? null}
          tools={
            <TreeTools
              label="Add a step"
              arming={arming}
              onArm={setArming}
              find={{
                steps: onBoard(tree).map((n) => ({ key: n.key, title: n.title })),
                onFind: (k) => setFound(k),
              }}
              end={saveState}
            />
          }
          aside={saveState}
          between={
            <>
              {sayLine}
              {lineAsk}
            </>
          }
          selected={found}
          keepEmpty
          draw={{ check, onLink: link }}
          onMoveToPhase={(k, phase) => edit(builderSetPhase(boxless(tree), k, phase), `Moved ${q(title(k))} into ${phaseName(phase)}.`)}
          onAddKind={add}
          arming={arming ? { kind: arming.kind, word: arming.as } : null}
          onLine={(l) => setAsked(l)}
          choosing={choose}
        />
      </div>
      {wide ? <PhasesEditor tree={tree} plan={plan} edit={edit} /> : <p className="meta mt-4">Lines and adding steps are for a wider screen.</p>}
    </Page>
  );
}
